import React, { useEffect, useRef } from 'react';
import { SUPPORT_CHANNELS } from '../config/support';

/**
 * Giriş ekranındaki "Yardıma ihtiyacın var mı?" penceresi.
 *
 * Neden ayrı bir pencere de sayfaya gömülü bir blok değil: destek numarası giriş
 * ekranında sürekli durursa, ekranın asıl işi olan tek giriş butonuyla dikkat
 * yarışına giriyor. Buradaysa yalnızca isteyen görüyor.
 *
 * İçerik `SUPPORT_CHANNELS` listesinden geliyor (config/support.ts) — uygulama
 * içindeki Yardım sayfalarıyla aynı kaynak, dolayısıyla numara tek yerde değişir.
 */
interface Props {
  open: boolean;
  onClose: () => void;
}

const SupportDialog: React.FC<Props> = ({ open, onClose }) => {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    // Pencere kapanınca odak, pencereyi AÇAN bağlantıya geri dönmeli. Aksi
    // hâlde klavyeyle gezen biri Esc'ledikten sonra sayfanın en başına düşüyor
    // ve giriş butonuna kadar baştan sekmek zorunda kalıyor.
    const previouslyFocused = document.activeElement as HTMLElement | null;
    panelRef.current?.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      previouslyFocused?.focus();
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      {/* Perde: tıklayınca kapanır. `aria-hidden` çünkü ekran okuyucu için bir
          içeriği yok — kapatma zaten Esc ve Kapat butonuyla da yapılabiliyor. */}
      <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden />

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="support-dialog-title"
        tabIndex={-1}
        className="relative w-full max-w-[380px] rounded-[24px] bg-[#fafafa] p-6 shadow-[0_12px_40px_rgba(0,0,0,0.18)] outline-none"
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Kapat"
          className="absolute right-4 top-4 flex h-[30px] w-[30px] items-center justify-center rounded-full text-[#5b6472] hover:bg-black/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#3973bf]"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>

        <h2
          id="support-dialog-title"
          className="pr-[34px] text-[17px] font-semibold text-[#2b2b2b]"
        >
          Yardıma mı ihtiyacın var?
        </h2>
        <p className="mt-[6px] text-[13px] leading-relaxed text-[#5b6472]">
          Giriş yapamıyorsan veya teknik bir sorun yaşıyorsan bize buradan ulaşabilirsin.
        </p>

        <div className="mt-[16px] space-y-[10px]">
          {SUPPORT_CHANNELS.map((c) => (
            <a
              key={c.id}
              href={c.href}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-start gap-[12px] rounded-[16px] border border-[#e9e9e9] bg-white px-[14px] py-[12px] transition-colors hover:border-[#25D366] hover:bg-[#f3fbf5] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#25D366]"
            >
              <span
                aria-hidden
                className="mt-[2px] flex h-[34px] w-[34px] flex-shrink-0 items-center justify-center rounded-full"
                style={{ backgroundColor: '#25D366' }}
              >
                <svg width="19" height="19" viewBox="0 0 24 24" fill="#ffffff">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347M12.05 21.785h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
              </span>
              <span className="min-w-0">
                <span className="block text-[14px] font-semibold text-[#2b2b2b]">{c.label}</span>
                <span className="block text-[14px] font-semibold text-[#25D366]">{c.value}</span>
                {c.hint && <span className="mt-[2px] block text-[12px] leading-snug text-[#5b6472]">{c.hint}</span>}
              </span>
            </a>
          ))}
        </div>

        {/* Giriş ekranından sorulabilecek tek şifre sorusunun cevabı. Yardım
            sayfasındaki SSS'de de var ama oraya ancak giriş yapınca ulaşılıyor —
            yani şifresini soran kişi tam olarak göremediği yere yönlendiriliyordu. */}
        <p className="mt-[16px] border-t border-[#e9e9e9] pt-[12px] text-[12px] leading-relaxed text-[#5b6472]">
          Şifreni mi unuttun? Şifren eis ekosis hesabında tutulur ve oradan
          değiştirilir; bu ekrandan sıfırlanamaz.
        </p>
      </div>
    </div>
  );
};

export default SupportDialog;
